import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { MessageCompose } from '@/components/chat/MessageCompose';
import { MessageGroup } from '@/components/chat/MessageGroup';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import { useSenderStore, useTyping } from '@/hooks';
import { useMessagesMutation, useMessagesQuery } from '@/hooks/useMessages';
import { useMessagesStore } from '@/hooks/useMessagesStore';
import { useScrollbar } from '@/hooks/useScrollbar';
import { useUnreadAgentMessage } from '@/hooks/useUnreadAgentMessage';
import { type Message, MessageStatus } from '@/types/message.types';
import { WidgetConfigResponse } from '@/types/widgetConfig';
import {
  formatMessageDate,
  getContactFormMessageMock,
  getWelcomeMessageMock,
} from '@/utils/message';

export interface WidgetWindowProps {
  widgetConfig: WidgetConfigResponse;
  onBack?: () => void;
  onMinimize: () => void;
  onContactOtherChannels?: (channel: string) => void;
}

const failedTooltipWidth = 210;
const failedDropdownWidth = 160;

const WidgetWindow: React.FC<WidgetWindowProps> = ({
  widgetConfig,
  onBack,
  onMinimize,
  onContactOtherChannels,
}) => {
  const scrollbar = useScrollbar();
  const { t } = useTranslation('common');
  const { settings } = widgetConfig;
  const {
    branding,
    preChatForm,
    additionalSettings: { welcomeMessage: welcomeMessageContent },
  } = settings;
  const agentDisplayName = branding.senderDisplayName;
  const agentAvatarCharacter = useMemo(
    () => branding.senderDisplayName.charAt(0).toUpperCase(),
    [branding.senderDisplayName],
  );
  const { isAgentTyping } = useTyping();
  const {
    fetchNextPage: fetchMoreMessages,
    hasNextPage: hasMoreMessages,
    isFetchingNextPage: isFetchingMoreMessages,
    isFetched: isInitialFetched,
  } = useMessagesQuery();
  const { handleRetryMessage } = useMessagesMutation();
  const { hasContactFormSubmitted } = useSenderStore();
  const { messages, messagesMap, visibleMessageGroups, dateDividerIndexes } =
    useMessagesStore();
  const { firstUnreadAgentMessageId } = useUnreadAgentMessage();
  const shadowRootRef = useRef<ShadowRoot>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const failedWarningRefs = useRef<Map<Message['id'], HTMLDivElement>>(
    new Map(),
  );
  const [failedDropdownId, setFailedDropdownId] = useState<
    Message['id'] | null
  >(null);
  const [hoverElPositions, setHoverElPositions] = useState<
    Map<
      Message['id'],
      {
        left: string;
        right: string;
      }
    >
  >(new Map());
  const isMessageComposeEnabled = useMemo(
    () =>
      !preChatForm?.enabled || hasContactFormSubmitted || messages.length > 0,
    [preChatForm, hasContactFormSubmitted, messages.length],
  );
  const shouldShowContactFormMessage = useMemo(
    () => !hasMoreMessages && preChatForm?.enabled && !hasContactFormSubmitted,
    [hasMoreMessages, preChatForm, hasContactFormSubmitted],
  );
  const shouldShowContactFormSubmittedMessage = useMemo(
    () => !hasMoreMessages && preChatForm?.enabled && hasContactFormSubmitted,
    [hasMoreMessages, preChatForm, hasContactFormSubmitted],
  );
  const shouldShowWelcomeMessage = useMemo(
    () =>
      !hasMoreMessages && (!preChatForm?.enabled || hasContactFormSubmitted),
    [hasMoreMessages, preChatForm, hasContactFormSubmitted],
  );
  const contactFormMessageGroup = useMemo(
    () => [getContactFormMessageMock(preChatForm)],
    [preChatForm],
  );
  const welcomeMessageGroup = useMemo(
    () => [getWelcomeMessageMock(welcomeMessageContent)],
    [welcomeMessageContent],
  );

  // cache shadow root on mount
  useEffect(() => {
    shadowRootRef.current =
      document.getElementById('sleekflow-widget-container')?.shadowRoot || null;
  }, []);

  // register dropdown dismiss event
  useEffect(() => {
    const widgetAppEl = shadowRootRef.current?.getElementById(
      'sleekflow-widget-app',
    );

    const handleClickOutside = (event: MouseEvent) => {
      if (failedDropdownId !== null) {
        const failedWarningEl = failedWarningRefs.current.get(failedDropdownId);

        if (
          failedWarningEl &&
          !failedWarningEl.contains(event.target as Node)
        ) {
          setFailedDropdownId(null);
        }
      }
    };

    widgetAppEl?.addEventListener('mousedown', handleClickOutside);
    return () => {
      widgetAppEl?.removeEventListener('mousedown', handleClickOutside);
    };
  }, [failedDropdownId]);

  // calculate hover positions failed messages
  useEffect(() => {
    setHoverElPositions((prevPositions) => {
      const newPositions = new Map(prevPositions);
      let hasNewPositions = false;

      // clean up positions for messages that no longer exist or are no longer failed
      for (const [messageId] of newPositions) {
        if (messagesMap.get(messageId)?.status !== MessageStatus.FAILED) {
          newPositions.delete(messageId);
          hasNewPositions = true;
        }
      }

      // calculate positions for new failed messages
      messages.forEach((message) => {
        if (
          !message.isSentFromSleekflow &&
          message.status === MessageStatus.FAILED &&
          !newPositions.has(message.id)
        ) {
          newPositions.set(message.id, calcHoverElPosition(message.id));
          hasNewPositions = true;
        }
      });

      return hasNewPositions ? newPositions : prevPositions;
    });
  }, [messages, messagesMap]);

  // auto-scroll to bottom when the last message changes/is added
  const lastMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (messagesContainerRef.current) {
      // use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: isInitialFetched ? 'smooth' : 'auto',
          });
        }
      });
    }
  }, [lastMessageId, isInitialFetched]);

  const calcHoverElPosition = (messageId: Message['id']) => {
    const messageContainerEl = shadowRootRef.current?.getElementById(
      `${messageId}`,
    );

    if (!messageContainerEl) return { left: 'auto', right: '0' };

    const containerWidth = messageContainerEl.offsetWidth;
    // use tooltip width since it's wider - if tooltip fits, dropdown will definitely fit
    return containerWidth > failedTooltipWidth
      ? { left: '0', right: 'auto' }
      : { left: 'auto', right: '0' };
  };

  const handleFetchMoreMessages = async () => {
    if (
      isInitialFetched &&
      !isFetchingMoreMessages &&
      messagesContainerRef.current
    ) {
      // save current scroll position before fetching
      const scrollTop = messagesContainerRef.current.scrollTop;
      const scrollHeight = messagesContainerRef.current.scrollHeight;

      await fetchMoreMessages();

      // restore scroll position after new messages are prepended
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const newScrollHeight = messagesContainerRef.current.scrollHeight;
          const heightDiff = newScrollHeight - scrollHeight;
          messagesContainerRef.current.scrollTop = scrollTop + heightDiff;
        }
      });
    }
  };

  const handleFailedWarningClick = useCallback((messageId: Message['id']) => {
    setFailedDropdownId(messageId);
  }, []);

  const handleRetryClick = useCallback(
    (messageId: Message['id']) => {
      handleRetryMessage(messageId);
      setFailedDropdownId(null);
    },
    [handleRetryMessage],
  );

  const renderContactFormSubmittedMessage = () => (
    <div className="sf:flex sf:justify-center sf:my-4">
      <div className="sf:px-2 sf:py-1 sf:rounded-full sf:w-fit sf:shadow-message-bubble sf:text-xs sf:select-none sf:bg-white/60 sf:text-content-secondary">
        {t('messages.contactFormSubmitted')}
      </div>
    </div>
  );

  const renderLoadMoreMessages = () => (
    <div className="sf:relative sf:flex sf:items-center sf:my-4">
      <div className="sf:flex-grow sf:border-t sf:border-horizontal-rule" />
      <button
        onClick={handleFetchMoreMessages}
        disabled={isFetchingMoreMessages}
        className="sf:mx-4 sf:text-xs sf:font-semibold sf:text-gray-600 sf:hover:text-gray-800 sf:transition-colors sf:duration-150 sf:select-none sf:cursor-pointer sf:disabled:opacity-50 sf:disabled:cursor-not-allowed sf:bg-transparent sf:border-none sf:p-0"
      >
        {isFetchingMoreMessages
          ? t('messages.loadingMore')
          : t('messages.loadMore')}
      </button>
      <div className="sf:flex-grow sf:border-t sf:border-horizontal-rule" />
    </div>
  );

  const renderDateDivider = (timestamp: number) => (
    <div className="sf:relative sf:flex sf:items-center sf:my-4">
      <div className="sf:flex-grow sf:border-t sf:border-horizontal-rule" />
      <p className="sf:mx-4 sf:text-xs sf:text-gray-600 sf:select-none">
        {formatMessageDate(timestamp, t)}
      </p>
      <div className="sf:flex-grow sf:border-t sf:border-horizontal-rule" />
    </div>
  );

  const renderUnreadAgentMessagesIndicator = () => (
    <div className="sf:relative sf:flex sf:items-center sf:my-4">
      <div className="sf:flex-grow sf:border-t sf:border-red-90" />
      <span className="sf:mx-4 sf:text-xs sf:text-red-90 sf:select-none sf:bg-transparent sf:border-none sf:p-0">
        {t('messages.newUnreadMessages')}
      </span>
      <div className="sf:flex-grow sf:border-t sf:border-red-90" />
    </div>
  );

  return (
    <div
      className={classNames(
        'sf:w-full sf:h-full sf:overflow-hidden sf:flex sf:flex-col sf:background-gradient-top',
        'sf:sm:w-[420px] sf:sm:max-w-md sf:sm:mx-auto sf:sm:rounded-2xl sf:sm:h-[680px]',
      )}
    >
      {/* Header */}
      <Header
        onBack={onBack}
        onMinimize={onMinimize}
        title={
          <div className="sf:flex sf:flex-col">
            <h1 className="sf:text-headline3">{branding.widgetDisplayName}</h1>
          </div>
        }
      />

      {/* Messages Area */}
      <div
        className="sf:flex-1 sf:overflow-y-auto sf:px-4 sf:pt-4 sf:pb-0 sf:space-y-3 sf:overscroll-contain"
        style={scrollbar.scrollbarStyle}
        onMouseEnter={scrollbar.onMouseEnter}
        onMouseLeave={scrollbar.onMouseLeave}
        ref={messagesContainerRef}
      >
        {/* Contact form submitted message */}
        {shouldShowContactFormSubmittedMessage &&
          renderContactFormSubmittedMessage()}

        {/* Contact form message */}
        {shouldShowContactFormMessage && (
          <MessageGroup
            messageGroup={contactFormMessageGroup}
            agentDisplayName={agentDisplayName}
            agentAvatarCharacter={agentAvatarCharacter}
          />
        )}

        {/* Welcome message */}
        {shouldShowWelcomeMessage && (
          <MessageGroup
            messageGroup={welcomeMessageGroup}
            agentDisplayName={agentDisplayName}
            agentAvatarCharacter={agentAvatarCharacter}
          />
        )}

        {/* Load more messages */}
        {hasMoreMessages && renderLoadMoreMessages()}

        {/* Messages */}
        {visibleMessageGroups.map((messageGroup, index) => (
          <div key={index}>
            {/* Date separator */}
            {messageGroup.some((message) =>
              dateDividerIndexes.has(message.id),
            ) && renderDateDivider(messageGroup[0].timestamp)}

            {/* Unread agent messages indicator */}
            {messageGroup.some(
              (message) => message.id === firstUnreadAgentMessageId,
            ) && renderUnreadAgentMessagesIndicator()}

            <MessageGroup
              messageGroup={messageGroup}
              agentDisplayName={agentDisplayName}
              agentAvatarCharacter={agentAvatarCharacter}
              failedDropdownId={failedDropdownId}
              failedDropdownWidth={failedDropdownWidth}
              failedTooltipWidth={failedTooltipWidth}
              failedWarningRefs={failedWarningRefs}
              hoverElPositions={hoverElPositions}
              handleRetryClick={handleRetryClick}
              handleFailedWarningClick={handleFailedWarningClick}
            />
          </div>
        ))}

        {/* Typing Indicator */}
        {isAgentTyping && (
          <div className="sf:flex sf:justify-start">
            <div className="sf:flex sf:items-start sf:gap-1">
              <div className="sf:w-7 sf:h-7 sf:rounded-full sf:flex sf:items-center sf:justify-center sf:flex-shrink-0 sf:mt-1 sf:bg-widget-button-color">
                <span className="sf:text-white sf:text-xs sf:font-medium">
                  {agentAvatarCharacter}
                </span>
              </div>

              <div className="sf:bg-white sf:bg-opacity-60 sf:rounded-xl sf:w-14 sf:h-8">
                <div className="sf:flex sf:justify-center sf:items-center sf:relative sf:px-3 sf:h-full">
                  <div className="sf:typing-indicator" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Compose */}
      {isMessageComposeEnabled && <MessageCompose />}

      {/* Footer */}
      <Footer onChannelSelect={onContactOtherChannels} />
    </div>
  );
};

export default WidgetWindow;
